import * as React from 'react';
import { useEffect, useRef, useState } from 'react';

/**
 * Renders an audiowaveform peaks JSON as a SoundCloud-style mirrored bar
 * waveform on a canvas. The `src` endpoint 302-redirects to a signed URL
 * serving the stored JSON (interleaved signed min/max pairs per channel per
 * pixel). Files without a waveform (all GCS-era files) resolve to nothing and
 * the component collapses — zero visual footprint.
 */
export interface WaveformProps {
  /** URL that resolves to the audiowaveform peaks JSON. */
  src: string;
  /** Played fraction 0..1 — bars up to this point are tinted. */
  progress?: number;
  /** Rendered height in CSS pixels. */
  height?: number;
  /** Bar color for the played portion; defaults to the Polaris emphasis token. */
  playedColor?: string;
  /** Bar color for the unplayed portion; defaults to the Polaris border token. */
  unplayedColor?: string;
}

interface StoredWaveform {
  length?: number;
  channels?: number;
  data?: number[];
}

// PremiumBeat-style rendering: dense 1px hairline bars with a playhead cursor
// line at the progress edge.
const BAR_WIDTH = 1;
const BAR_GAP = 1;

/**
 * Collapse the stored interleaved min/max pairs into one 0..1 magnitude per
 * pixel: max over channels of max(|min|, |max|), auto-gained by the loudest
 * peak so the shape fills the height regardless of bit depth.
 */
const toPeaks = (raw: StoredWaveform | null | undefined): number[] => {
  if (!raw || !Array.isArray(raw.data) || raw.data.length === 0) {
    return [];
  }
  const channels = Number(raw.channels) > 0 ? Math.floor(Number(raw.channels)) : 1;
  const stride = 2 * channels;
  const count = Math.floor(raw.data.length / stride);
  if (count < 1) {
    return [];
  }

  const peaks: number[] = new Array(count);
  let max = 0;
  for (let pixel = 0; pixel < count; pixel++) {
    const base = pixel * stride;
    let peak = 0;
    for (let channel = 0; channel < channels; channel++) {
      const lo = Math.abs(Number(raw.data[base + channel * 2]) || 0);
      const hi = Math.abs(Number(raw.data[base + channel * 2 + 1]) || 0);
      const amplitude = Math.max(lo, hi);
      if (amplitude > peak) peak = amplitude;
    }
    peaks[pixel] = peak;
    if (peak > max) max = peak;
  }

  if (max <= 0) {
    return [];
  }
  return peaks.map((peak) => peak / max);
};

/** Downsample (or pad) the peaks to exactly `bars` buckets, taking the max per bucket. */
const bucketPeaks = (peaks: number[], bars: number): number[] => {
  if (bars < 1 || peaks.length === 0) {
    return [];
  }
  const buckets: number[] = new Array(bars).fill(0);
  const ratio = peaks.length / bars;
  for (let bar = 0; bar < bars; bar++) {
    const start = Math.floor(bar * ratio);
    const end = Math.max(start + 1, Math.floor((bar + 1) * ratio));
    let peak = 0;
    for (let i = start; i < end && i < peaks.length; i++) {
      if (peaks[i] > peak) peak = peaks[i];
    }
    buckets[bar] = peak;
  }
  return buckets;
};

const cssVar = (name: string, fallback: string): string => {
  if (typeof window === 'undefined' || !window.getComputedStyle) {
    return fallback;
  }
  const value = window.getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value || fallback;
};

const Waveform: React.FC<WaveformProps> = ({
  src,
  progress = 0,
  height = 56,
  playedColor,
  unplayedColor,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [peaks, setPeaks] = useState<number[] | null>(null);
  const [status, setStatus] = useState<'loading' | 'loaded' | 'error'>('loading');

  // Fetch + parse the peaks JSON. Any failure collapses the component.
  useEffect(() => {
    let active = true;
    setStatus('loading');
    setPeaks(null);

    if (!src) {
      setStatus('error');
      return;
    }

    fetch(src)
      .then((res) => {
        if (!res.ok) throw new Error(`waveform fetch failed: ${res.status}`);
        return res.json();
      })
      .then((json) => {
        if (!active) return;
        const parsed = toPeaks(json);
        if (parsed.length === 0) {
          setStatus('error');
          return;
        }
        setPeaks(parsed);
        setStatus('loaded');
      })
      .catch(() => {
        if (active) setStatus('error');
      });

    return () => {
      active = false;
    };
  }, [src]);

  // Draw (and redraw on resize / progress change).
  useEffect(() => {
    if (status !== 'loaded' || !peaks || peaks.length === 0) {
      return;
    }
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) {
      return;
    }

    const draw = () => {
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const dpr = window.devicePixelRatio || 1;
      const width = container.clientWidth || peaks.length * (BAR_WIDTH + BAR_GAP);
      canvas.width = Math.max(1, Math.floor(width * dpr));
      canvas.height = Math.max(1, Math.floor(height * dpr));
      canvas.style.width = '100%';
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, width, height);

      const bars = Math.max(1, Math.floor(width / (BAR_WIDTH + BAR_GAP)));
      const buckets = bucketPeaks(peaks, bars);
      const mid = height / 2;
      const played = playedColor ?? cssVar('--p-color-text-emphasis', '#6554ff');
      const unplayed = unplayedColor ?? cssVar('--p-color-border', '#c9cdd4');
      const clampedProgress = Math.min(1, Math.max(0, progress));
      const progressX = width * clampedProgress;

      for (let bar = 0; bar < buckets.length; bar++) {
        const x = bar * (BAR_WIDTH + BAR_GAP);
        // Keep a hairline even for silent buckets so the track reads as full-width.
        const barHeight = Math.max(1, buckets[bar] * (height - 2));
        ctx.fillStyle = x < progressX ? played : unplayed;
        ctx.fillRect(x, mid - barHeight / 2, BAR_WIDTH, barHeight);
      }

      // Playhead cursor.
      if (clampedProgress > 0 && clampedProgress < 1) {
        ctx.fillStyle = played;
        ctx.fillRect(Math.min(progressX, width - 1.5), 0, 1.5, height);
      }
    };

    draw();

    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => draw());
      observer.observe(container);
    } else if (typeof window !== 'undefined') {
      window.addEventListener('resize', draw);
    }

    return () => {
      if (observer) {
        observer.disconnect();
      } else if (typeof window !== 'undefined') {
        window.removeEventListener('resize', draw);
      }
    };
  }, [status, peaks, progress, height, playedColor, unplayedColor]);

  // Absent or failed — render nothing so the layout is unchanged for GCS-era files.
  if (status === 'error') {
    return null;
  }

  return (
    <div
      ref={containerRef}
      className='track-waveform'
      style={{ width: '100%', height }}
      aria-hidden='true'
    >
      {status === 'loading' ? (
        <div
          className='track-waveform__skeleton'
          style={{
            width: '100%',
            height: '100%',
            borderRadius: 6,
            background:
              'linear-gradient(90deg, var(--p-color-bg-surface-secondary, #f1f1f1) 0%, var(--p-color-bg-surface-tertiary, #e3e3e3) 50%, var(--p-color-bg-surface-secondary, #f1f1f1) 100%)',
            opacity: 0.6,
          }}
        />
      ) : (
        <canvas ref={canvasRef} style={{ display: 'block', width: '100%', height }} />
      )}
    </div>
  );
};

export default Waveform;
